import { schemas } from '@repo/contracts'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import { optionalProjectId } from '../../common/optionalFields'
import type { z } from 'zod'

// -- UI enum value map (kept hand-written; RagFilePage references e.g. RagFileUploadState.Processing).
// The generated z.enum(...) inside apihttp_RagFileItemV2 governs wire parsing; this map is a UI
// convenience and stays type-compatible (same string-literal union). --

export const RagFileUploadState = {
  Unknown: 'unknown',
  Uploading: 'uploading',
  Processing: 'processing',
  Complete: 'complete',
  Error: 'error'
} as const

export type RagFileUploadState = (typeof RagFileUploadState)[keyof typeof RagFileUploadState]

// -- Wire types (generated; @repo/contracts is the source of truth) --

export type RagFile = z.infer<typeof schemas.apihttp_RagFileItemV2>
export type GetRagFileResponse = z.infer<typeof schemas.apihttp_SearchFileV2Response>
export type FetchRagFiles = z.input<typeof schemas.apihttp_SearchRequestV2>

const defaultFetchParams: FetchRagFiles = {
  currentPage: 1
}

export async function fetchRagFiles(params: Partial<FetchRagFiles> = {}): Promise<GetRagFileResponse> {
  const body: FetchRagFiles = { ...defaultFetchParams, ...params }
  const res = await fetch('/api/content/file/searchv2', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_SearchFileV2Response)
}

export async function addRagFile(file: File, description: string, projectId?: number): Promise<number> {
  const registerRes = await fetch('/api/content/file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      fileName: file.name,
      description,
      tagIds: [],
      ...optionalProjectId(projectId)
    })
  })
  await apiErrorHandler(registerRes)
  const registerData: unknown = await registerRes.json()
  const { fileID, preSignedUrl } = parseDataOfType(registerData, schemas.apihttp_AddFileResponse)

  const uploadRes = await fetch(preSignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  })
  if (!uploadRes.ok) {
    throw new Error(`File upload failed: ${uploadRes.statusText}`)
  }
  return fileID
}

export async function updateRagFile(fileId: number, description: string, projectId?: number): Promise<void> {
  const res = await fetch(`/api/content/file/${fileId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ description, tagIds: [], ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

export async function deleteRagFile(fileId: number, projectId?: number): Promise<void> {
  const url =
    projectId !== undefined ? `/api/content/file/${fileId}?projectId=${projectId}` : `/api/content/file/${fileId}`
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function getRagFileDownloadUrl(fileId: number): Promise<string> {
  const res = await fetch(`/api/content/file/${fileId}/download`, {
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  const parsed = parseDataOfType(data, schemas.apihttp_FileDownloadResponse)
  return parsed.downloadUrl
}

export async function retryRagFileProcessing(fileId: number): Promise<void> {
  const res = await fetch(`/api/content/file/${fileId}/retry-processing`, {
    method: 'POST',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}
